import { Link } from 'react-router-dom'
import {
  Banknote,
  CalendarDays,
  ClipboardList,
  FileText,
  LayoutDashboard,
  Package,
  Plus,
  Receipt,
  SquareCheck,
  Store,
  Users,
  type LucideIcon,
} from 'lucide-react'
import type { Permission } from '@b4l/shared'
import { numeDepartament, vedeDeconturileEchipei } from '@b4l/shared'
import { useDepartamente } from '../taskuri/date'
import { useNoutati } from './date'
import { esteSolicitare, esteTaskTerminat, useCitesteNotificarea } from '../../lib/notificari'

interface Scurtatura {
  to: string
  eticheta: string
  icon: LucideIcon
  /** Fara dreptul paginii, scurtatura nu apare — n-ar duce decat la „interzis". */
  drept?: Permission
  /** Peste drept, doar pentru aceste roluri (deconturile tuturor). */
  doarDaca?: (role: string) => boolean
}

const S = {
  comandaNoua: {
    to: '/operational/comenzi/nou',
    eticheta: 'Comandă nouă',
    icon: Plus,
    drept: 'orders:write',
  },
  taskuri: { to: '/task-uri', eticheta: 'Task-uri', icon: SquareCheck, drept: 'tasks:read' },
  cereriMarfa: {
    to: '/marketing/cereri-marfa',
    eticheta: 'Cereri de marfă',
    icon: Package,
    drept: 'marketing.sampling:read',
  },
  rambursuri: {
    to: '/compliance/rambursuri-curieri',
    eticheta: 'Rambursuri',
    icon: Banknote,
    drept: 'fan:read',
  },
  incasari: {
    to: '/compliance/incasari',
    eticheta: 'Încasări bancare',
    icon: Banknote,
    drept: 'compliance:read',
  },
  facturiRestante: {
    to: '/compliance/facturi-restante',
    eticheta: 'Facturi restante',
    icon: FileText,
    drept: 'compliance:read',
  },
  tabloulVanzari: {
    to: '/operational/cockpit-vanzari',
    eticheta: 'Tablou vânzări',
    icon: LayoutDashboard,
    drept: 'operational.agenti:read',
  },
  clientiiMei: {
    to: '/clienti',
    eticheta: 'Clienții mei',
    icon: Users,
    drept: 'orders:read',
  },
  alocare: {
    to: '/operational/alocare-clienti',
    eticheta: 'Alocare clienți',
    icon: Users,
    drept: 'operational.alocare:read',
  },
  decontulMeu: { to: '/hr/incarca-bon', eticheta: 'Decontul meu', icon: Receipt },
  deconturi: {
    to: '/hr/deconturi',
    eticheta: 'Deconturi',
    icon: Receipt,
    drept: 'hr.deconturi:read',
    doarDaca: vedeDeconturileEchipei,
  },
  ordine: {
    to: '/hr/ordine-deplasare',
    eticheta: 'Ordine deplasare',
    icon: ClipboardList,
    drept: 'hr.ordine_deplasare:read',
  },
  intalniri: {
    to: '/intalniri',
    eticheta: 'Întâlniri',
    icon: CalendarDays,
    drept: 'meetings:read',
  },
  tipografie: {
    to: '/marketing/cereri-print',
    eticheta: 'Tipografie',
    icon: FileText,
    drept: 'marketing.print:read',
  },
  expuneri: {
    to: '/marketing/expuneri',
    eticheta: 'Expuneri',
    icon: Store,
    drept: 'marketing.sampling:read',
  },
  inventar: {
    to: '/operational/inventar-diferente',
    eticheta: 'Inventar',
    icon: ClipboardList,
    drept: 'operational.inventar:read',
  },
} satisfies Record<string, Scurtatura>

/** Actiunile rolului, nu meniul a doua oara. Primele patru pe care omul are voie sa le deschida. */
const PE_ROL: Record<string, Scurtatura[]> = {
  admin: [S.comandaNoua, S.taskuri, S.cereriMarfa, S.rambursuri],
  contabilitate: [S.rambursuri, S.incasari, S.facturiRestante, S.taskuri],
  vanzari: [S.comandaNoua, S.tabloulVanzari, S.clientiiMei, S.decontulMeu],
  manager_vanzari: [S.alocare, S.tabloulVanzari, S.comandaNoua, S.taskuri],
  marketing: [S.tipografie, S.cereriMarfa, S.expuneri, S.taskuri],
  depozit: [S.taskuri, S.inventar, S.decontulMeu, S.cereriMarfa],
  administrativ: [S.deconturi, S.ordine, S.intalniri, S.taskuri],
}

export function scurtaturiPentru(role: string | undefined, permissions: readonly string[]) {
  return (PE_ROL[role ?? ''] ?? [S.taskuri, S.decontulMeu])
    .filter((s) => !s.drept || permissions.includes(s.drept))
    .filter((s) => !s.doarDaca || s.doarDaca(role ?? ''))
    .slice(0, 4)
}

export function Scurtaturi({ lista }: { lista: Scurtatura[] }) {
  return (
    <div className="grid gap-1.5">
      {lista.map((s) => (
        <Link
          key={s.to}
          to={s.to}
          className="flex min-h-[44px] items-center gap-2 rounded-md border border-b4l-border px-2.5 py-2 text-corp text-b4l-text hover:border-b4l-blue/60 hover:bg-b4l-bg-soft"
        >
          <s.icon size={16} className="shrink-0 text-b4l-text-dim" aria-hidden="true" />
          <span className="min-w-0 truncate">{s.eticheta}</span>
        </Link>
      ))}
    </div>
  )
}

/** Pe telefon: scurtaturile jos, la indemana degetului (Madalin, 13.10.2026). */
export function BaraJos({ lista }: { lista: Scurtatura[] }) {
  if (lista.length === 0) return null
  return (
    <nav
      aria-label="Scurtături"
      className="sticky bottom-0 z-20 -mx-4 grid border-t border-b4l-border bg-b4l-surface px-1 pb-[max(env(safe-area-inset-bottom),6px)] pt-1 sm:hidden"
      style={{ gridTemplateColumns: `repeat(${lista.length}, minmax(0, 1fr))` }}
    >
      {lista.map((s) => (
        <Link
          key={s.to}
          to={s.to}
          className="grid min-h-[52px] justify-items-center gap-0.5 rounded-md px-1 py-1.5 text-center text-[11px] leading-tight text-b4l-text-muted active:bg-b4l-bg"
        >
          <s.icon size={20} aria-hidden="true" />
          <span className="line-clamp-2">{s.eticheta}</span>
        </Link>
      ))}
    </nav>
  )
}

const deCand = (iso: string) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (min < 1) return 'acum'
  if (min < 60) return `acum ${min} min`
  const ore = Math.round(min / 60)
  if (ore < 24) return `acum ${ore} ${ore === 1 ? 'oră' : 'ore'}`
  return new Date(iso).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })
}

/** Ce s-a intamplat cu munca ta — notificarile tale, nu ale firmei. Fara actiune. */
export function Noutati() {
  const q = useNoutati()
  const citeste = useCitesteNotificarea()
  const ultimele = (q.data?.notifications ?? []).slice(0, 5)
  if (q.isLoading) return <p className="text-mic text-b4l-text-dim">Se încarcă…</p>
  if (q.isError)
    return <p className="text-mic text-b4l-text-muted">Noutățile nu s-au putut încărca.</p>
  if (ultimele.length === 0)
    return <p className="text-mic text-b4l-text-muted">Nimic nou pentru tine.</p>
  return (
    <ul className="grid gap-2">
      {ultimele.map((n) => {
        /*
          O solicitare necitita (cineva asteapta ceva de la tine) iese in rosu: Victor, 19.10.2026,
          „sa avem o culoare pronuntata pentru orice tip de solicitare noua". O informare necitita
          — doar mai apasata. Citite, toate gri, in afara de task-urile terminate, care raman verzi.
        */
        const solicitare = !n.readAt && esteSolicitare(n.kind)
        // Task terminat: verde, citit sau nu (Victor, 21.10.2026).
        const terminat = esteTaskTerminat(n.kind)
        const continut = (
          <>
            {solicitare && (
              <span className="text-eticheta font-semibold uppercase tracking-wide text-b4l-red-dark">
                Solicitare nouă
              </span>
            )}
            <span
              className={`text-corp ${
                solicitare
                  ? 'font-semibold text-b4l-red-dark'
                  : terminat
                    ? `${n.readAt ? '' : 'font-semibold '}text-b4l-green-dark`
                    : n.readAt
                      ? 'text-b4l-text-muted'
                      : 'font-medium text-b4l-text'
              }`}
            >
              {n.title}
            </span>
            <span className="text-mic text-b4l-text-dim">{deCand(n.createdAt)}</span>
          </>
        )
        const stil = `grid gap-0.5 rounded-md ${
          solicitare
            ? 'border-l-[3px] border-b4l-red bg-b4l-red-light px-2 py-1.5'
            : terminat
              ? `border-l-[3px] border-b4l-green px-2 py-1.5 ${n.readAt ? '' : 'bg-b4l-green-light'}`
              : !n.readAt
                ? 'border-l-[3px] border-b4l-blue pl-2'
                : ''
        }`
        return (
          <li key={n.id}>
            {n.link ? (
              <Link
                to={n.link}
                // Deschisa de aici e citita, ca din clopotel.
                onClick={() => !n.readAt && citeste.mutate(n.id)}
                className={`${stil} hover:text-b4l-blue`}
              >
                {continut}
              </Link>
            ) : (
              <span className={stil}>{continut}</span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

const BUCATI = [
  { cheie: 'dePreluat', nume: 'De preluat', culoare: 'bg-b4l-amber' },
  { cheie: 'inLucru', nume: 'În lucru', culoare: 'bg-b4l-blue' },
  { cheie: 'asteapta', nume: 'Așteaptă', culoare: 'bg-b4l-purple' },
] as const

/** Tabloul departamentului, pe scurt: cate sunt in fiecare coloana si cate sunt depasite. */
export function TablouDepartament({ admin }: { admin: boolean }) {
  const q = useDepartamente()
  if (q.isLoading) return <p className="text-mic text-b4l-text-dim">Se încarcă…</p>
  if (q.isError || !q.data)
    return <p className="text-mic text-b4l-text-muted">Tabloul nu s-a putut încărca.</p>
  const deps = admin
    ? q.data.departamente
    : q.data.departamente.filter((d) => d.slug === q.data.departamentulMeu)
  if (deps.length === 0)
    return <p className="text-mic text-b4l-text-muted">Rolul tău nu are un departament.</p>
  return (
    <div className="grid gap-2.5">
      {deps.map((d) => {
        const total = BUCATI.reduce((t, b) => t + d[b.cheie], 0)
        return (
          <Link
            key={d.slug}
            to={`/task-uri?dep=${d.slug}`}
            className="grid gap-1 rounded-md hover:bg-b4l-bg-soft sm:grid-cols-[120px_minmax(0,1fr)_auto] sm:items-center sm:gap-3"
          >
            <span className="text-corp font-medium text-b4l-text">{numeDepartament(d.slug)}</span>
            <span
              className="flex h-2.5 overflow-hidden rounded-full bg-b4l-bg"
              role="img"
              aria-label={BUCATI.map((b) => `${b.nume} ${d[b.cheie]}`).join(', ')}
            >
              {total > 0 &&
                BUCATI.map((b) =>
                  d[b.cheie] > 0 ? (
                    <span
                      key={b.cheie}
                      className={b.culoare}
                      style={{ width: `${(d[b.cheie] / total) * 100}%` }}
                    />
                  ) : null,
                )}
            </span>
            <span className="text-mic tabular-nums text-b4l-text-muted">
              {total} {total === 1 ? 'deschis' : 'deschise'}
              {d.depasite > 0 && <span className="text-b4l-red"> · {d.depasite} depășite</span>}
            </span>
          </Link>
        )
      })}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-mic text-b4l-text-dim">
        {BUCATI.map((b) => (
          <span key={b.cheie} className="inline-flex items-center gap-1">
            <i className={`h-2 w-2 rounded-full ${b.culoare}`} aria-hidden="true" />
            {b.nume}
          </span>
        ))}
      </div>
    </div>
  )
}
